import { DateTime } from "luxon";
import { useDashboardSummary } from "./use-dashboard-summary";
import { useCurrentUser } from "@/lib/api/user";

const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

export function MentorWorkflowsEmptyDashboard() {
  const dateTime = DateTime.now().toFormat("cccc, MMMM d");
  const { data: user } = useCurrentUser();
  const greeting = `Hi, ${user?.name ?? "there"}!`;

  const { teens, unread, checkIns, isLoading } = useDashboardSummary();

  const stats = [
    { label: "UNREAD", value: isLoading ? "—" : String(unread) },
    { label: "CHECK-INS", value: isLoading ? "—" : String(checkIns) },
    { label: "TEENS", value: isLoading ? "—" : String(teens) },
  ];

  const heading = isLoading
    ? "\u00A0"
    : checkIns > 0
      ? `${checkIns} ${pluralize(checkIns, "teen", "teens")} to reconnect with`
      : "You're all caught up";

  let subtitle: string;
  if (isLoading) {
    subtitle = "\u00A0";
  } else if (unread > 0) {
    subtitle = `You have ${unread} unread ${pluralize(
      unread,
      "message",
      "messages",
    )}.`;
  } else if (checkIns > 0) {
    subtitle = "No new messages, but some teens could use a check-in.";
  } else {
    subtitle = "No new messages right now.";
  }

  return (
    <section
      aria-label="Mentor dashboard empty state"
      className="flex min-w-0 flex-1 items-stretch py-4 pl-2 pr-4"
    >
      <div className="flex min-w-0 flex-1 items-center justify-center overflow-hidden rounded-2xl bg-white pb-6 shadow-[0_0_16px_rgb(0_0_0/0.08)]">
        <div className="w-full max-w-[512px] px-6">
          <p className="text-xs leading-[1.4] text-text-tertiary">{dateTime}</p>
          <h1 className="font-avantt mt-3 text-4xl font-bold leading-[1.1] tracking-[-0.9px] text-text-primary">
            {greeting}
          </h1>
          <div className="mt-5 h-px w-full bg-surfaces-z4" />
          <h2 className="mt-6 text-xl leading-[1.4] text-text-primary">
            {heading}
          </h2>
          <p className="mt-2 text-base leading-[1.4] text-text-tertiary">
            {subtitle}
          </p>
          <dl className="mt-8 flex gap-12 whitespace-nowrap">
            {stats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-0.5">
                <dt className="order-2 text-xs leading-[1.4] text-text-tertiary">
                  {stat.label}
                </dt>
                <dd className="font-avantt text-[32px] font-bold leading-[1.05] tracking-[-0.8px] text-text-primary">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}
